<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  IconCheck,
  IconCircleCheckFilled,
  IconFlame,
  IconMessageCircle,
  IconPhoto,
  IconQuoteFilled,
  IconUsers,
  IconVideo,
  IconX,
} from '@tabler/icons-vue'

import { useAuthClient } from '@/stores/auth-store'
import { useMessageStore } from '@/stores/message-store.ts'

const messageStore = useMessageStore()
const { post } = useAuthClient()
const activeFilter = ref<'all' | 'recent' | 'drafts'>('all')
const searchQuery = ref('')
const isSearchPanelOpen = ref(false)
const searchPanelRef = ref<HTMLElement | null>(null)
const weeklyStreak = [
  { day: 'Sun', active: false },
  { day: 'Mon', active: true },
  { day: 'Tue', active: true },
  { day: 'Wed', active: false },
  { day: 'Thu', active: true },
  { day: 'Fri', active: false },
  { day: 'Sat', active: true },
]
const streakCount = computed(() => weeklyStreak.filter((item) => item.active).length)

const orderedMessages = computed(() => [...(messageStore.messages ?? [])].reverse())
const mockMessages = [
  {
    id: 'mock-alex-hike',
    type: 'COMMENT',
    metadata: {
      author: 'Alex Rivera',
      timeline: 'Grand Canyon Trip',
      edited: '8m ago',
      kind: 'photo',
      summary: 'Sunrise from the South Rim was unreal. Added a couple of shots from this morning.',
      eventTitle: 'South Rim sunrise walk',
      avatarUrl: 'https://i.pravatar.cc/300?img=32',
      photos: [
        'https://picsum.photos/seed/gcanyon-1/320/240',
        'https://picsum.photos/seed/gcanyon-2/320/240',
      ],
    },
  },
  {
    id: 'mock-maya-birthday',
    type: 'COMMENT',
    metadata: {
      author: 'Maya Chen',
      timeline: 'Luca Turns 5',
      edited: '21m ago',
      kind: 'photo',
      summary: 'Cake reveal was a hit. Uploading the best pics from the backyard setup.',
      eventTitle: 'Birthday cake moment',
      avatarUrl: 'https://i.pravatar.cc/300?img=47',
      photos: [
        'https://picsum.photos/seed/bday-1/320/240',
        'https://picsum.photos/seed/bday-2/320/240',
        'https://picsum.photos/seed/bday-3/320/240',
      ],
    },
  },
]
const feedMessages = computed(() => [...mockMessages, ...orderedMessages.value])
const filteredMessages = computed(() => {
  const filter = activeFilter.value
  const scopedMessages = feedMessages.value.filter((message) => {
    if (filter === 'recent') {
      const edited = getEditedText(message).toLowerCase()
      return edited.includes('m ago') || edited.includes('h ago') || edited === 'just now'
    }

    if (filter === 'drafts') {
      return message.type === 'AI' && !message.completed
    }

    return true
  })

  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return scopedMessages

  return scopedMessages.filter((message) => {
    const searchableText = [
      getAuthorName(message),
      getEventTitle(message),
      getSummaryText(message),
      getActivityLabel(message),
      getEditedText(message),
      getEventTitleText(message) ?? '',
    ]
      .join(' ')
      .toLowerCase()

    return searchableText.includes(query)
  })
})

function openSearchPanel() {
  isSearchPanelOpen.value = true
}

function clearSearch() {
  searchQuery.value = ''
}

function onDocumentClick(event: MouseEvent) {
  const target = event.target as Node | null
  if (!target || !searchPanelRef.value) return
  if (!searchPanelRef.value.contains(target)) {
    isSearchPanelOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
})

function getMetadata(message: any) {
  if (message?.metadata && typeof message.metadata === 'object') return message.metadata
  if (message?.message && typeof message.message === 'object') return message.message
  return {}
}

function getAuthorName(message: any) {
  const metadata = getMetadata(message)
  return metadata.author ?? metadata.name ?? 'TimeThread'
}

function getEventTitle(message: any) {
  const metadata = getMetadata(message)
  return metadata.timeline ?? metadata.subject ?? metadata.title ?? 'Timeline Update'
}

function getSummaryText(message: any) {
  const metadata = getMetadata(message)
  if (typeof metadata.quote === 'string') return metadata.quote
  if (typeof metadata.summary === 'string') return metadata.summary
  if (typeof message?.message === 'string') return message.message
  return 'New details are ready for your timeline.'
}

function getEditedText(message: any) {
  const metadata = getMetadata(message)
  return metadata.edited ?? metadata.timeAgo ?? 'just now'
}

function getActivityKind(message: any): 'comment' | 'event' | 'photo' | 'video' {
  const metadata = getMetadata(message)
  const rawKind = `${metadata.kind ?? metadata.type ?? ''}`.toLowerCase()
  if (rawKind.includes('video')) return 'video'
  if (rawKind.includes('photo') || rawKind.includes('image')) return 'photo'
  if (rawKind.includes('event')) return 'event'
  return 'comment'
}

function getActivityVerb(message: any) {
  const kind = getActivityKind(message)
  if (kind === 'event') return ' shared an event in'
  if (kind === 'photo') return ' shared photos in'
  if (kind === 'video') return ' shared a video in'
  return 'commented in'
}

function getActivityIcon(message: any) {
  const kind = getActivityKind(message)
  if (kind === 'photo') return IconPhoto
  if (kind === 'video') return IconVideo
  return IconMessageCircle
}

function getActivityLabel(message: any) {
  const kind = getActivityKind(message)
  if (kind === 'event') return 'Event'
  if (kind === 'photo') return 'Photos'
  if (kind === 'video') return 'Video'
  return 'Comment'
}

function getPhotos(message: any): string[] {
  const metadata = getMetadata(message)
  if (!Array.isArray(metadata.photos)) return []
  return metadata.photos.filter((photo: unknown) => typeof photo === 'string')
}

function getVideos(message: any): string[] {
  const metadata = getMetadata(message)
  if (!Array.isArray(metadata.videos)) return []
  return metadata.videos.filter((video: unknown) => typeof video === 'string')
}

function getEventTitleText(message: any) {
  const metadata = getMetadata(message)
  return metadata.eventTitle ?? metadata.event ?? metadata.title ?? null
}

function getAvatarUrl(message: any) {
  const metadata = getMetadata(message)
  return (
    metadata.avatarUrl ??
    metadata.avatar ??
    'https://testingbot.com/free-online-tools/random-avatar/300?img=11'
  )
}

async function createEvent(message: any) {
  try {
    const response = await post('/rpc/api/event/new', getMetadata(message))
    if (!response.ok) {
      console.error(`Error: ${response.status}`)
      return null
    }
    message.completed = true
    return await response.json()
  } catch (err) {
    console.log(err)
    return null
  }
}
</script>

<template>
  <div class="h-full overflow-y-auto bg-base-200/30 p-4 md:p-6">
    <div class="mx-auto max-w-2xl space-y-4">
      <section ref="searchPanelRef" class="relative">
        <div class="relative w-full">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search activity..."
            class="w-full rounded-full border border-base-300 bg-base-100 py-2 pl-10 pr-10 text-base-content ring-1 ring-base-content/10 placeholder:text-base-content/60 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500"
            @focus="openSearchPanel"
            @click="openSearchPanel"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/60"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
            <path d="M21 21l-6 -6" />
          </svg>
          <button
            v-if="searchQuery"
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-base-content/60 transition-colors hover:text-base-content"
            @click="clearSearch"
          >
            <IconX class="h-4 w-4" />
          </button>
        </div>

        <div
          v-if="isSearchPanelOpen"
          class="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-20 rounded-2xl border border-base-300 bg-base-100 p-3 shadow-lg"
        >
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="badge h-7 border transition-colors"
              :class="
                activeFilter === 'all'
                  ? 'badge-primary border-primary text-primary-content'
                  : 'badge-outline border-base-300 text-base-content'
              "
              @click="activeFilter = 'all'"
            >
              All
            </button>
            <button
              type="button"
              class="badge h-7 border transition-colors"
              :class="
                activeFilter === 'recent'
                  ? 'badge-primary border-primary text-primary-content'
                  : 'badge-outline border-base-300 text-base-content'
              "
              @click="activeFilter = 'recent'"
            >
              Recent
            </button>
            <button
              type="button"
              class="badge h-7 border transition-colors"
              :class="
                activeFilter === 'drafts'
                  ? 'badge-primary border-primary text-primary-content'
                  : 'badge-outline border-base-300 text-base-content'
              "
              @click="activeFilter = 'drafts'"
            >
              Drafts
            </button>
          </div>
        </div>
      </section>

      <section class="rounded-2xl border border-base-300 bg-base-100 px-4 py-4 shadow-sm">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2">
            <div class="rounded-lg bg-primary/10 p-1.5 text-primary">
              <IconFlame class="h-4 w-4" />
            </div>
            <div>
              <h2 class="text-sm font-semibold">Weekly streak</h2>
              <p class="text-xs text-base-content/70">Days you added an event or memory</p>
            </div>
          </div>
          <span class="badge badge-primary badge-outline">{{ streakCount }}/7 days</span>
        </div>

        <div class="mt-4 grid grid-cols-7 gap-2">
          <div v-for="item in weeklyStreak" :key="item.day" class="space-y-1 text-center">
            <div class="h-2 rounded-full" :class="item.active ? 'bg-primary' : 'bg-base-300'" />
            <p class="text-[10px] font-semibold uppercase tracking-wide text-base-content/60">
              {{ item.day }}
            </p>
          </div>
        </div>
      </section>

<!--      <section>-->
<!--        <div class="join w-full">-->
<!--          <button-->
<!--            type="button"-->
<!--            class="join-item btn flex-1"-->
<!--            :class="activeFilter === 'all' ? 'btn-primary' : 'btn-outline'"-->
<!--            @click="activeFilter = 'all'"-->
<!--          >-->
<!--            All-->
<!--          </button>-->
<!--          <button-->
<!--            type="button"-->
<!--            class="join-item btn flex-1"-->
<!--            :class="activeFilter === 'recent' ? 'btn-primary' : 'btn-outline'"-->
<!--            @click="activeFilter = 'recent'"-->
<!--          >-->
<!--            Recent-->
<!--          </button>-->
<!--          <button-->
<!--            type="button"-->
<!--            class="join-item btn flex-1"-->
<!--            :class="activeFilter === 'drafts' ? 'btn-primary' : 'btn-outline'"-->
<!--            @click="activeFilter = 'drafts'"-->
<!--          >-->
<!--            Drafts-->
<!--          </button>-->
<!--        </div>-->
<!--      </section>-->

      <template v-if="filteredMessages.length > 0">
        <div class="flex items-center gap-2 pb-3">
          <span class="text-sm font-semibold uppercase tracking-wide text-primary/90">Today</span>
          <div class="h-1 flex-1 rounded-full bg-primary/80"></div>
        </div>

        <div
          v-for="message in filteredMessages"
          :key="
            message.id ?? `${message.type}-${getEditedText(message)}-${getSummaryText(message)}`
          "
        >
          <div v-if="message.type === 'MINE'" class="flex justify-end">
            <div
              class="max-w-md rounded-3xl rounded-tr-lg bg-primary px-5 py-3 text-primary-content shadow-sm"
            >
              {{ getSummaryText(message) }}
            </div>
          </div>

          <div v-else-if="message.type === 'PROCESSING'" class="flex items-start gap-4">
            <span class="loading loading-dots loading-md text-primary"></span>
            <div class="rounded-2xl bg-base-100 px-4 py-3 text-sm text-base-content/70 shadow-sm">
              Creating your next timeline draft...
            </div>
          </div>

          <article v-else class="grid grid-cols-[auto_1fr] items-start gap-3">
            <div class="avatar indicator">
              <span class="indicator-item badge badge-primary badge-xs"></span>
              <div class="h-14 w-14 rounded-full border border-base-300">
                <img :src="getAvatarUrl(message)" :alt="`${getAuthorName(message)} avatar`" />
              </div>
            </div>

            <div class="space-y-3">
              <div class="leading-tight">
                <span class="font-semibold text-base-content">{{ getAuthorName(message) }}</span>
                <span class="text-base-content/80"> {{ getActivityVerb(message) }}&nbsp;</span>
                <span class="font-semibold text-primary">{{ getEventTitle(message) }}</span>
                <p class="mt-1 text-sm text-base-content/50">{{ getEditedText(message) }}</p>
              </div>

              <div class="rounded-2xl bg-base-100 px-4 py-3 text-base-content shadow-sm">
                <div class="relative mt-3 rounded-xl bg-base-200 px-4 py-4">
                  <IconQuoteFilled
                    class="absolute -left-1 -top-2 h-5 w-5 rotate-180 text-primary/70"
                  />
                  <p class="px-3 text-base leading-relaxed">
                    {{ getSummaryText(message) }}
                  </p>
                  <IconQuoteFilled class="absolute -bottom-2 right-1 h-5 w-5 text-primary/70" />
                </div>

                <div
                  v-if="getEventTitleText(message)"
                  class="mt-3 rounded-xl bg-base-200/70 px-3 py-2 text-sm"
                >
                  <span class="font-semibold text-base-content">Event:</span>
                  <span class="text-base-content/80"> {{ getEventTitleText(message) }}</span>
                </div>

                <div v-if="getPhotos(message).length > 0" class="mt-3 flex items-center gap-2">
                  <img
                    v-for="(photo, index) in getPhotos(message).slice(0, 3)"
                    :key="`${photo}-${index}`"
                    :src="photo"
                    alt="Timeline photo"
                    class="h-14 w-14 rounded-xl object-cover"
                  />
                  <div
                    v-if="getPhotos(message).length > 3"
                    class="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-lg font-semibold text-primary"
                  >
                    +{{ getPhotos(message).length - 3 }}
                  </div>
                </div>

                <div v-if="getVideos(message).length > 0" class="mt-3 flex items-center gap-2">
                  <div
                    v-for="(video, index) in getVideos(message).slice(0, 2)"
                    :key="`${video}-${index}`"
                    class="relative h-20 w-28 overflow-hidden rounded-xl border border-base-300 bg-base-200"
                  >
                    <video :src="video" class="h-full w-full object-cover" muted></video>
                    <div class="absolute inset-0 flex items-center justify-center bg-black/30">
                      <IconVideo class="h-5 w-5 text-white" />
                    </div>
                  </div>
                </div>
                <div class="mt-4">
                  <div class="mb-3 flex items-center gap-2">
                    <div class="rounded-lg bg-primary/10 p-1.5 text-primary">
                      <component :is="getActivityIcon(message)" class="h-4 w-4" />
                    </div>
                    <span
                      class="text-xs font-semibold uppercase tracking-wide text-base-content/60"
                    >
                      {{ getActivityLabel(message) }}
                    </span>
                  </div>

                  <div
                    v-if="message.type === 'AI'"
                    class="mt-3 flex items-center justify-between border-t border-base-300 pt-3"
                  >
                    <div class="flex items-center gap-2 text-sm text-base-content/70">
                      <IconCircleCheckFilled
                        v-if="message.completed"
                        class="h-5 w-5 text-success"
                      />
                      <span>{{ message.completed ? 'Saved as event' : 'Save as new event?' }}</span>
                    </div>
                    <div v-if="!message.completed" class="flex items-center gap-1">
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm text-success"
                        @click="createEvent(message)"
                      >
                        <IconCheck class="h-4 w-4" />
                      </button>
                      <button type="button" class="btn btn-ghost btn-sm text-error">
                        <IconX class="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>

        <div class="flex items-center gap-2 pt-1">
          <span class="text-sm font-semibold uppercase tracking-wide text-primary/90"
            >Yesterday</span
          >
          <div class="h-1 flex-1 rounded-full bg-primary/80"></div>
        </div>
      </template>

      <template v-else>
        <div class="flex h-full min-h-[16rem] items-center justify-center">
          <div class="rounded-full bg-primary/10 px-8 py-4 font-medium text-primary">
            No Messages
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
